"use client"

import { useWindowScroll } from "@mantine/hooks"
import { AnimatePresence, motion } from "motion/react"
import { useTranslations } from "next-intl"
import { parseAsStringEnum, useQueryState } from "nuqs"
import { type ComponentProps, type SetStateAction, useEffect, useState } from "react"
import { Button } from "~/components/legacy/common/button"
import { Stack } from "~/components/legacy/common/stack"
import { Tooltip } from "~/components/legacy/common/tooltip"
import { CodeXmlIcon, FlagIcon } from "~/components/ui/icons"
import { ToolEmbedDialog } from "~/components/web/dialogs/tool-embed-dialog"
import { ToolReportDialog } from "~/components/web/dialogs/tool-report-dialog"
import { ToolButton } from "~/components/web/tools/tool-button"
import { reportsConfig } from "~/config/reports"
import { isToolApproved, isToolPublished } from "~/lib/legacy-tools"
import { cx } from "~/lib/legacy-utils"
import type { ToolOne } from "~/server/web/tools/payloads"

type ToolActionsProps = ComponentProps<typeof Stack> & {
  tool: ToolOne
}

enum Dialog {
  report = "report",
  embed = "embed",
}

export const ToolActions = ({ tool, children, className, ...props }: ToolActionsProps) => {
  const t = useTranslations("tools.actions")
  const [scroll] = useWindowScroll()
  const [dialog, setDialog] = useQueryState("dialog", parseAsStringEnum(Object.values(Dialog)))
  const [isStickyButtonVisible, setIsStickyButtonVisible] = useState(false)

  useEffect(() => {
    if (isToolPublished(tool)) {
      setIsStickyButtonVisible(scroll.y > 250)
    }
  }, [scroll])

  const handleClose = (isOpen: SetStateAction<boolean>) => {
    !isOpen && setDialog(null)
  }

  return (
    <Stack size="sm" wrap={false} className={cx("justify-end", className)} {...props}>
      <AnimatePresence>
        {isStickyButtonVisible && (
          <motion.div
            variants={{
              hidden: { opacity: 0, x: -4, scale: 0.9 },
              visible: { opacity: 1, x: 0, scale: 1 },
            }}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ type: "spring", duration: 0.3, ease: "easeInOut" }}
            className="order-first origin-right max-md:hidden"
          >
            <ToolButton tool={tool} size="md">
              <span className="@max-xl:sr-only">{t("visit_button")}</span>
            </ToolButton>
          </motion.div>
        )}
      </AnimatePresence>

      {reportsConfig.enabled && (
        <Tooltip tooltip={t("report_tooltip")}>
          <Button
            size="md"
            variant="secondary"
            prefix={<FlagIcon />}
            onClick={() => setDialog(Dialog.report)}
            aria-label={t("report_button")}
          />
        </Tooltip>
      )}

      {isToolApproved(tool) && (
        <Tooltip tooltip={t("embed_tooltip")}>
          <Button
            size="md"
            variant="secondary"
            prefix={<CodeXmlIcon />}
            onClick={() => setDialog(Dialog.embed)}
            aria-label={t("embed_button")}
          />
        </Tooltip>
      )}

      {children}

      {reportsConfig.enabled && (
        <ToolReportDialog tool={tool} isOpen={dialog === Dialog.report} setIsOpen={handleClose} />
      )}

      {isToolApproved(tool) && (
        <ToolEmbedDialog tool={tool} isOpen={dialog === Dialog.embed} setIsOpen={handleClose} />
      )}
    </Stack>
  )
}
